import { TableComponent } from '../../ui/TableComponent';
import { useMemo, useState } from 'react';
import { TopSection } from '../../ui/TopSection';
import { useLoadOrderHistory, useLoadRevenueHistory } from '../../../utils/dataLoadingUtils';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { usePriceAlertsStore } from '../../../stores/appPriceAlertsStore';
import { useAppUserDefaultStore, saveVatOrdersSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterOrdersByDateRange, filterRevenueByDateRange } from '../../../utils/dateRangeFilters';
import { calculateTotalSpent, calculateFullSupplierSpending, calculateAverageSpent } from '../../../utils/orderHistoryCalculations';
import { calculateTotalRevenue } from '../../../utils/revenueHistoryCalculations';
import { calculateCostPercentage, calculateCostChange, calculateCostPercentageChange } from '../../../utils/kpiCalculations';
import { getPreviousPeriodRange, calculatePercentageChange } from '../../../utils/kpiChangeCalculations';
import { calculateSpendingSplit, calculateCategorySpentBreakdown } from '../../../utils/chartsUtils';
import { filterRevenueByOverallType } from '../../revenuehistory/utils';
import { SpendingSplitBar } from '../../orderhistory/SpendingSplitBar';
import { ByRestaurantCard, ByRestaurantRow } from '../../ui/ByRestaurantCard';
import { SpendBySupplierCard } from '../../orderhistory/SpendBySupplierCard';
import { SpendByCategoryDonut } from '../../orderhistory/SpendByCategoryDonut';
import { SPLIT_FOOD, SPLIT_DRINKS } from '../reports/hub/common';
import { filterOrdersByOverallType, toDate, getAvailablePlatforms, getAvailableCategories } from '../../orderhistory/utils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { View } from '../../../components/orderhistory/types';
import { StatCard } from '../../ui/StatCard';
import { getRankBadgeStyle, primaryText, smallTextSize, mediumText, tertiaryText, semiBoldText } from '../../../styling/stylingHelper';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { currencyFormatter } from '../../../services/helperService';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { cn } from '../../../lib/utils';
import { metricIcon } from '../../ui/metricIcons';
import { usePrecomputedProductAnalytics, fastTopProductsAggregation } from '../../orderhistory/aggregations';
import { getOrderTotal } from '../../orderhistory/vatUtils';
import { getTableFormattedDate } from '../../../utils/dateUtils';
import { useOrderFilters } from '../../../hooks/useOrderFilters';
import { filterAllOrders } from '../../../utils/filterUtils';
import { saveCurrentScrollPosition, resetScrollToTop } from '../../../utils/scrollUtils';
import { useEffect } from 'react';
import { isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useTranslation } from 'react-i18next';
import RestaurantNamesCell from '../../orderhistory/RestaurantNamesCell';

// Non-food slice color for the spending-split bar (food/drinks reuse the shared SPLIT_* tints).
const SPLIT_NONFOOD = '#98A2B3';

// Elevated card surface for the mobile card lists (matches the shipped Dashboard/Revenue mobile cards).
const MOBILE_CARD =
  'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Connect to stores
  const orderHistoryStore = useOrderHistory();
  const revenueHistoryStore = useRevenueHistory();
  const priceAlertsStore = usePriceAlertsStore();
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const revenueSourcesMap = useRestaurantStore(s => s.restaurantRevenueSources);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    const availableOptions = ['all', 'food', 'drinks', 'non-food'];
    if (overallTypeFilter && !availableOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Order filters hook - include all filters for this page
  const {
    filters,
    setPlatformFilters,
    setSectionFilters,
    setCategoryFilters,
    resetFilters,
    activeFilterCount,
  } = useOrderFilters({
    includeRestaurants: isAllRestaurants || false,
    includePlatforms: true,
    includeSections: true,
    includeCategories: true,
  });

  // Time controls - centralized logic. Declared before the data loading hook so the
  // progressive loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load data progressively: the selected window (default last week) renders first, then
  // the rest of the history streams in the background. Revenue is loaded too so the
  // all-restaurants Cost % KPI has a denominator (single-restaurant ignores it).
  useLoadOrderHistory({ progressive: true, viewStart: startDate });
  useLoadRevenueHistory({ progressive: true, viewStart: startDate });

  // Progressive-loading state: "loading" means the currently viewed period hasn't fully
  // streamed in yet. The all-time view can only be trusted once everything is loaded.
  const ordersFirstChunkLoaded = useOrderHistory(s => s.firstChunkLoaded);
  const ordersFullyLoaded = useOrderHistory(s => s.fullyLoaded);
  const ordersLoadedFromDate = useOrderHistory(s => s.loadedFromDate);
  const isOrdersLoading =
    !ordersFullyLoaded && (
      !ordersFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!ordersLoadedFromDate && startDate < ordersLoadedFromDate)
    );

  const revenueFirstChunkLoaded = useRevenueHistory(s => s.firstChunkLoaded);
  const revenueFullyLoaded = useRevenueHistory(s => s.fullyLoaded);
  const revenueLoadedFromDate = useRevenueHistory(s => s.loadedFromDate);
  const isRevenueLoading =
    !revenueFullyLoaded && (
      !revenueFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!revenueLoadedFromDate && startDate < revenueLoadedFromDate)
    );

  // Filter data by date range
  const dateFilteredOrders = useMemo(() =>
    filterOrdersByDateRange(orderHistoryStore.orderHistoryList, startDate, endDate),
    [orderHistoryStore.orderHistoryList, startDate, endDate]
  );

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks' | 'non-food');
  };

  // Handler for VAT toggle - updates for orders VAT setting
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await Promise.all([
        saveVatOrdersSetting(showExVat)
      ]);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Apply overall type filter, then order filters (platforms, categoryType, categories)
  const overallTypeFilteredOrders = useMemo(() =>
    filterOrdersByOverallType(dateFilteredOrders, overallTypeFilter),
    [dateFilteredOrders, overallTypeFilter]
  );

  const filteredOrders = useMemo(() =>
    filterAllOrders(
      overallTypeFilteredOrders,
      filters,
      isAllRestaurants ? restaurantFilters : undefined,
      platformToSectionIndex
    ),
    [overallTypeFilteredOrders, filters, isAllRestaurants, restaurantFilters, platformToSectionIndex]
  );

  // Calculate KPIs
  const totalSpent = useMemo(() =>
    calculateTotalSpent(filteredOrders, showExVatOrders),
    [filteredOrders, showExVatOrders]
  );


  // ──────────────────────────────────────────────────────────────────────────
  // All-restaurants KPIs & left-column data (cross-restaurant). Only meaningful
  // in all-restaurants mode; the single-restaurant branch keeps its own KPIs.
  // ──────────────────────────────────────────────────────────────────────────

  // Revenue source → restaurant lookup, for scoping revenue by the restaurant filter.
  const revSourceToRestaurant = useMemo(() => {
    const lookup: Record<string, string> = {};
    Object.entries(revenueSourcesMap).forEach(([rId, sources]) => {
      sources.forEach(source => { lookup[source.id] = rId; });
    });
    return lookup;
  }, [revenueSourcesMap]);

  // Revenue scoped exactly like the orders (date + restaurant filter + overall type),
  // for the Cost % KPI. In single-restaurant mode the store already holds only that
  // restaurant's revenue, so no restaurant scoping is needed there.
  const filteredRevenueForCost = useMemo(() => {
    const restaurantScoped = (isAllRestaurants && restaurantFilters.length > 0)
      ? revenueHistoryStore.revenueHistoryList.filter(rev => {
          const rId = rev.restaurantId || revSourceToRestaurant[rev.restaurantRevenueSourceId];
          return rId && restaurantFilters.includes(rId);
        })
      : revenueHistoryStore.revenueHistoryList;
    const dateScoped = filterRevenueByDateRange(restaurantScoped, startDate, endDate);
    return filterRevenueByOverallType(dateScoped, overallTypeFilter);
  }, [isAllRestaurants, revenueHistoryStore.revenueHistoryList, restaurantFilters, revSourceToRestaurant, startDate, endDate, overallTypeFilter]);

  const totalRevenue = useMemo(() =>
    calculateTotalRevenue(filteredRevenueForCost, showExVatRevenue),
    [filteredRevenueForCost, showExVatRevenue]
  );

  const costPercentage = useMemo(() =>
    calculateCostPercentage(totalSpent, totalRevenue),
    [totalSpent, totalRevenue]
  );

  // Distinct restaurants with spend in the period (avg-spend denominator).
  const activeRestaurantCount = useMemo(() => {
    if (!isAllRestaurants) return 0;
    const ids = new Set<string>();
    filteredOrders.forEach(order => {
      const rId = order.restaurantSection?.restaurantId || order.restaurantId;
      if (rId) ids.add(rId);
    });
    return ids.size;
  }, [isAllRestaurants, filteredOrders]);

  const avgSpendPerRestaurant = activeRestaurantCount > 0 ? totalSpent / activeRestaurantCount : 0;

  // ── Period-over-period change for the KPI cards ────────────────────────────
  // Un-date-scoped lists mirroring the filtered KPIs (minus the date filter) so
  // the change helpers can slice current vs previous period themselves.
  const baseOrdersFiltered = useMemo(() => {
    const typed = filterOrdersByOverallType(orderHistoryStore.orderHistoryList, overallTypeFilter);
    return filterAllOrders(
      typed,
      filters,
      isAllRestaurants ? restaurantFilters : undefined,
      platformToSectionIndex
    );
  }, [orderHistoryStore.orderHistoryList, overallTypeFilter, filters, isAllRestaurants, restaurantFilters, platformToSectionIndex]);

  const baseRevenueFiltered = useMemo(() => {
    const restaurantScoped = (isAllRestaurants && restaurantFilters.length > 0)
      ? revenueHistoryStore.revenueHistoryList.filter(rev => {
          const rId = rev.restaurantId || revSourceToRestaurant[rev.restaurantRevenueSourceId];
          return rId && restaurantFilters.includes(rId);
        })
      : revenueHistoryStore.revenueHistoryList;
    return filterRevenueByOverallType(restaurantScoped, overallTypeFilter);
  }, [isAllRestaurants, revenueHistoryStore.revenueHistoryList, restaurantFilters, revSourceToRestaurant, overallTypeFilter]);

  // Average spend per period (day/week/month) across the full history, mirroring the
  // revenue page's "Average Revenue" KPI. Shown on the single-restaurant page.
  const averageSpent = useMemo(() =>
    calculateAverageSpent(baseOrdersFiltered, showExVatOrders, selectedTimeOption),
    [baseOrdersFiltered, showExVatOrders, selectedTimeOption]
  );

  // Total spend change (shared by all-restaurants "Total Spend" + single "Total Spent").
  const spendChange = useMemo(() =>
    calculateCostChange(totalSpent, selectedTimeOption as View, startDate, endDate, baseOrdersFiltered, showExVatOrders),
    [totalSpent, selectedTimeOption, startDate, endDate, baseOrdersFiltered, showExVatOrders]
  );

  // Cost % change (all-restaurants).
  const costPercentageChange = useMemo(() =>
    calculateCostPercentageChange(costPercentage, selectedTimeOption as View, startDate, endDate, baseOrdersFiltered, baseRevenueFiltered, showExVatOrders, showExVatRevenue),
    [costPercentage, selectedTimeOption, startDate, endDate, baseOrdersFiltered, baseRevenueFiltered, showExVatOrders, showExVatRevenue]
  );

  // Static spending split (food/drinks/non-food) for the left-column bar.
  const spendingSplitSegments = useMemo(() => {
    const splitFilter = overallTypeFilter === 'food' ? 'food' : overallTypeFilter === 'drinks' ? 'drinks' : 'all';
    const meta: Record<string, { color: string; key: string }> = {
      'Food': { color: SPLIT_FOOD, key: 'overallType.food' },
      'Drinks': { color: SPLIT_DRINKS, key: 'overallType.drinks' },
      'Non-Food': { color: SPLIT_NONFOOD, key: 'overallType.nonFood' },
    };
    return calculateSpendingSplit(filteredOrders, splitFilter, showExVatOrders).map(slice => ({
      label: meta[slice.name] ? t(meta[slice.name].key) : slice.name,
      pct: slice.percentage,
      color: meta[slice.name]?.color || '#a3a3a3',
    }));
  }, [filteredOrders, overallTypeFilter, showExVatOrders, t]);

  // Restaurant id → name, for the Recent Orders restaurant column.
  const restaurantNameById = useMemo(() =>
    new Map(restaurants.map(r => [r.id, r.name])),
    [restaurants]
  );

  // Most recent orders across all restaurants. The table paginates (5/page) so users can
  // page through the full set and see the total count — no row clicks.
  const recentOrdersRows = useMemo(() => {
    return [...filteredOrders]
      .sort((a, b) => toDate(b.orderDate).getTime() - toDate(a.orderDate).getTime())
      .map(order => {
        const rId = order.restaurantSection?.restaurantId || order.restaurantId;
        return {
          date: getTableFormattedDate(toDate(order.orderDate)),
          restaurant: (rId && restaurantNameById.get(rId)) || order.restaurantName || '--',
          platform: order.platform,
          unknownSupplierName: order.unknownSupplierName,
          products: (order.productData || []).length,
          total: getOrderTotal(order, showExVatOrders),
          id: order.id,
        };
      });
  }, [filteredOrders, restaurantNameById, showExVatOrders]);

  // Spend by restaurant across all restaurants: share of total (bar) + period-over-period
  // change pill, matching the revenue-by-restaurant card. The 'all' view has no previous
  // period, so the change pill is hidden.
  const spendByRestaurantRows = useMemo<ByRestaurantRow[]>(() => {
    if (!isAllRestaurants) return [];
    const nameMap = new Map(restaurants.map(r => [r.id, r.name]));
    const groupSpend = (orders: typeof filteredOrders) => {
      const m = new Map<string, number>();
      for (const o of orders) {
        const rId = o.restaurantSection?.restaurantId || o.restaurantId;
        if (!rId) continue;
        m.set(rId, (m.get(rId) || 0) + getOrderTotal(o, showExVatOrders));
      }
      return m;
    };
    const currentMap = groupSpend(filteredOrders);
    const prevRange = getPreviousPeriodRange(selectedTimeOption as View, startDate, endDate);
    const prevOrders = prevRange
      ? filterOrdersByDateRange(baseOrdersFiltered, prevRange.previousStartDate, prevRange.previousEndDate)
      : [];
    const prevMap = groupSpend(prevOrders);
    const total = Array.from(currentMap.values()).reduce((s, v) => s + v, 0);
    const hideChange = selectedTimeOption === 'all';
    return Array.from(currentMap.entries())
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([rId, value]) => ({
        restaurantId: rId,
        restaurantName: nameMap.get(rId) || rId,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0,
        changePercent: hideChange ? null : calculatePercentageChange(value, prevMap.get(rId) || 0),
      }));
  }, [isAllRestaurants, filteredOrders, baseOrdersFiltered, restaurants, showExVatOrders, selectedTimeOption, startDate, endDate]);

  // Spend by category (donut + maximize modal). Shown in both single- and all-restaurants views.
  const categorySpendBreakdown = useMemo(() =>
    calculateCategorySpentBreakdown(filteredOrders, showExVatOrders, t),
    [filteredOrders, showExVatOrders, t]
  );

  // Spend-by-restaurant ExpandModal — page-owned so the `?expand=spendByRestaurant`
  // deep link from the Dashboard Highest-Spend ranking card can open it.
  const [spendByRestaurantModalOpen, setSpendByRestaurantModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('expand') === 'spendByRestaurant') {
      setSpendByRestaurantModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('expand');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Calculate supplier spending data
  const supplierSpending = useMemo(() =>
    calculateFullSupplierSpending(filteredOrders, showExVatOrders),
    [filteredOrders, showExVatOrders]
  );

  // Pre-compute analytics ONCE per period (massive performance boost!)
  const { avgSpentExVat, avgSpentIncVat, avgQuantity } = usePrecomputedProductAnalytics(
    orderHistoryStore.orderHistoryList,
    startDate,
    selectedTimeOption as View
  );

  // Calculate top products using fast aggregation function
  const topProductsAggregation = useMemo(() => {
    const averageSpentMap = showExVatOrders ? avgSpentExVat : avgSpentIncVat;
    return fastTopProductsAggregation(
      filteredOrders,
      averageSpentMap,
      avgQuantity,
      priceAlertsStore.priceAlerts,
      startDate,
      selectedTimeOption as View,
      showExVatOrders
    );
  }, [filteredOrders, showExVatOrders, avgSpentExVat, avgSpentIncVat, avgQuantity, priceAlertsStore.priceAlerts, startDate, selectedTimeOption]);

  // Top products by spend across all restaurants (preview; "See all" → products page).
  // Uses the same aggregation as the single-restaurant table so the quantity is the
  // standardized kg/L/piece amount and we get the per-product restaurant list (hoverable).
  const topProductSpendRows = useMemo(() => {
    return topProductsAggregation.map((product, index) => ({
      rank: index + 1,
      name: product.name,
      restaurantNames: product.restaurantNames || [],
      quantity: product.formattedQuantity || '--',
      unitPrice: product.unitPrice?.displayText || '--',
      total: product.spend,
    }));
  }, [topProductsAggregation]);

  const handleOrderRowClick = (row: any) => {
    if (row.id) {
      // Save scroll position before navigating to detail page
      saveCurrentScrollPosition();
      navigate(`/orderhistory/${row.id}`);
    }
  };

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available sections from the restaurant store
  // Get available sections from the restaurant store
  const availableSections = useMemo(() => {
    if (!restaurantId || !restaurantSectionsMap[restaurantId]) return [];
    return restaurantSectionsMap[restaurantId].map(s => ({ id: s.id, name: s.name }));
  }, [restaurantId, restaurantSectionsMap]);

  // Get available filter options from the overall-type-filtered data (before applying order filters)
  const availablePlatforms = useMemo(() =>
    getAvailablePlatforms(overallTypeFilteredOrders),
    [overallTypeFilteredOrders]
  );

  const availableCategories = useMemo(() =>
    getAvailableCategories(overallTypeFilteredOrders),
    [overallTypeFilteredOrders]
  );

  return (
    <div>
      <MobilePageHeader title={t('orderHistory.title')} right={<RestaurantSelector />} />

      <TopSection
        title={t('orderHistory.title')}
        backButton={false}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        onOverallTypeChange={handleOverallTypeChange}
        currentOverallTypeValue={overallTypeFilter}
        overallTypeOptions={['all', 'food', 'drinks', 'non-food']}
        onVatChange={handleVatChange}
        currentVatValue={showExVatOrders ? 'excl' : 'incl'}
        showFilters={true}
        filters={{
          orderFilters: {
            restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
            setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
            availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
            platformFilters: filters.platforms,
            setPlatformFilters,
            availablePlatforms,
            sectionFilters: filters.sections,
            setSectionFilters,
            availableSections,
            categoryFilters: filters.categories,
            setCategoryFilters,
            availableCategories,
            onResetFilters: resetFilters,
            activeFilterCount,
          }
        }}
      />

      {/* Responsive layout group for content sections below 1250px */}
      <div className="mt-4 mb-4 flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below (compact rows with tighter gap on phones) */}
        <div className="flex flex-col gap-3 sm:gap-6 min-[1250px]:flex-row" data-onboarding="page-kpis" data-onboarding-ready={!isOrdersLoading && !isRevenueLoading}>
          {isAllRestaurants ? (
            <>
              <div className="w-full min-[1250px]:flex-1">
                <StatCard
                  {...metricIcon('spend')}
                  title={t('orderHistory.totalSpend')}
                  value={totalSpent}
                  valueType="currency"
                  changeValue={spendChange?.changeValue}
                  changeLabel={spendChange?.changeLabel}
                  trendDirection="down"
                  loading={isOrdersLoading}
                />
              </div>
              <div className="w-full min-[1250px]:flex-1">
                <StatCard
                  {...metricIcon('avgSpend')}
                  title={t('orderHistory.avgSpendPerRestaurant')}
                  value={avgSpendPerRestaurant}
                  valueType="currency"
                  loading={isOrdersLoading}
                />
              </div>
              <div className="w-full min-[1250px]:flex-1">
                <StatCard
                  {...metricIcon('costPercent')}
                  title={t('orderHistory.costPercentage')}
                  value={costPercentage}
                  valueType="percentage"
                  changeValue={costPercentageChange?.changeValue}
                  changeLabel={costPercentageChange?.changeLabel}
                  trendDirection="down"
                  loading={isOrdersLoading || isRevenueLoading}
                />
              </div>
            </>
          ) : (
            <>
              <div className="w-full min-[1250px]:flex-1">
                <StatCard
                  {...metricIcon('spend')}
                  title={t('orderHistory.totalSpent')}
                  value={totalSpent}
                  valueType="currency"
                  changeValue={spendChange?.changeValue}
                  changeLabel={spendChange?.changeLabel}
                  trendDirection="down"
                  loading={isOrdersLoading}
                />
              </div>
              <div className="w-full min-[1250px]:flex-1">
                <StatCard
                  {...metricIcon('avgSpend')}
                  title={t('orderHistory.avgSpent')}
                  value={averageSpent}
                  valueType="currency"
                  loading={isOrdersLoading}
                />
              </div>
              <div className="w-full min-[1250px]:flex-1">
                <StatCard
                  {...metricIcon('costPercent')}
                  title={t('orderHistory.costPercentage')}
                  value={costPercentage}
                  valueType="percentage"
                  changeValue={costPercentageChange?.changeValue}
                  changeLabel={costPercentageChange?.changeLabel}
                  trendDirection="down"
                  loading={isOrdersLoading || isRevenueLoading}
                />
              </div>
            </>
          )}
        </div>

        {/* Left column (top products / spending split / recent orders) + right column
            (spend by restaurant [all-restaurants only] / supplier / category breakdowns). */}
        <div className="flex flex-col gap-6 min-[1250px]:flex-row min-[1250px]:items-start">
          <div className="w-full flex flex-col gap-6 min-[1250px]:flex-1">
            <div data-onboarding="orders-top-products" data-onboarding-ready={!isOrdersLoading}>
            <ResponsiveTable
              desktop={<TableComponent
                title={t('orderHistory.topProductsBySpend')}
                data={topProductSpendRows}
                blankLines={false}
                usePagination={true}
                itemsPerPage={5}
                columns={[
                  {
                    key: 'name',
                    label: t('dashboard.product'),
                    columnType: 'name',
                    render: (value, row) => (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                          style={getRankBadgeStyle(row.rank || 1)}
                        >
                          {row.rank || 1}
                        </div>
                        <span className={cn(primaryText, smallTextSize, mediumText)}>{String(value)}</span>
                      </div>
                    ),
                  },
                  // "Restaurants" column only makes sense across restaurants.
                  ...(isAllRestaurants ? [{
                    key: 'restaurantNames' as const,
                    label: t('orderHistory.restaurantCount'),
                    columnType: 'base' as const,
                    render: (value: any) => <RestaurantNamesCell restaurantNames={value} />,
                  }] : []),
                  { key: 'quantity', label: t('orderHistory.quantity'), columnType: 'blueQuantity' },
                  { key: 'unitPrice', label: t('orderHistory.unitPrice'), columnType: 'base' },
                  { key: 'total', label: t('orderHistory.total'), columnType: 'currencyHighlightBlack' },
                ]}
                seeAllText={t('orderHistory.viewProducts')}
                seeAllOnboardingId="orders-view-products"
                onSeeAllClick={() => {navigate('/orderhistory/products')}}
              />}
              mobile={
                <div>
                  <h3 className={cn('text-[18px] leading-6 mb-4', semiBoldText, primaryText)}>{t('orderHistory.topProductsBySpend')}</h3>
                  <MobileCardList
                    items={topProductSpendRows}
                    keyOf={(row, i) => `${row.name}-${i}`}
                    previewCount={3}
                    onViewAll={() => navigate('/orderhistory/products')}
                    viewAllLabel={t('orderHistory.viewProducts')}
                    renderCard={(row) => (
                      <div className={cn(MOBILE_CARD, 'flex flex-col gap-3')}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                              style={getRankBadgeStyle(row.rank || 1)}
                            >
                              {row.rank || 1}
                            </div>
                            <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)}>{row.name}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn('text-xs font-medium', tertiaryText)}>{t('orderHistory.total')}</span>
                            <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format(row.total)}</span>
                          </div>
                        </div>
                        <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                          {isAllRestaurants && (
                            <MobileCardField label={t('orderHistory.restaurantCount')} value={String(row.restaurantNames.length)} divider />
                          )}
                          <MobileCardField label={t('orderHistory.quantity')} value={row.quantity} divider />
                          <MobileCardField label={t('orderHistory.unitPrice')} value={row.unitPrice} />
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
            </div>

            <div data-onboarding="orders-spending-split" data-onboarding-ready={!isOrdersLoading}>
            <SpendingSplitBar
              title={t('orderHistory.spendingSplit')}
              segments={spendingSplitSegments}
              loading={isOrdersLoading}
              emptyText={t('common.noDataInPeriod')}
            />
            </div>

            <ResponsiveTable
              desktop={<TableComponent
                title={t('orderHistory.recentOrders')}
                data={recentOrdersRows}
                blankLines={false}
                usePagination={true}
                itemsPerPage={5}
                columns={[
                  { key: 'date', label: t('orderHistory.date'), columnType: 'base' },
                  // Restaurant column only makes sense across restaurants.
                  ...(isAllRestaurants ? [{ key: 'restaurant' as const, label: t('dashboard.restaurant'), columnType: 'name' as const }] : []),
                  { key: 'platform', label: t('orderHistory.supplierTableHeader'), columnType: 'platform' },
                  { key: 'products', label: t('orderHistory.products'), columnType: 'base' },
                  { key: 'total', label: t('orderHistory.total'), columnType: 'currencyHighlightBlack' },
                ]}
                seeAllText={t('common.seeAll')}
                onSeeAllClick={() => {navigate('/orderhistory/orders')}}
                onRowClick={isAllRestaurants ? undefined : handleOrderRowClick}
              />}
              mobile={
                <div>
                  <h3 className={cn('text-[18px] leading-6 mb-4', semiBoldText, primaryText)}>{t('orderHistory.recentOrders')}</h3>
                  <MobileCardList
                    items={recentOrdersRows}
                    keyOf={(row, i) => `${row.id ?? i}`}
                    previewCount={3}
                    onViewAll={() => navigate('/orderhistory/orders')}
                    viewAllLabel={t('common.seeAll')}
                    renderCard={(row) => (
                      <div
                        className={cn(MOBILE_CARD, 'flex flex-col gap-3', !isAllRestaurants && 'cursor-pointer')}
                        onClick={!isAllRestaurants ? () => handleOrderRowClick(row) : undefined}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {isAllRestaurants ? (
                              <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)}>{row.restaurant}</span>
                            ) : (
                              <SupplierLogo platform={row.platform} unknownSupplierName={row.unknownSupplierName} size="medium" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn('text-xs font-medium', tertiaryText)}>{t('orderHistory.total')}</span>
                            <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format(row.total)}</span>
                          </div>
                        </div>
                        <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                          {isAllRestaurants && (
                            <MobileCardField
                              label={t('orderHistory.supplierTableHeader')}
                              value={<SupplierLogo platform={row.platform} unknownSupplierName={row.unknownSupplierName} size="small" />}
                              divider
                            />
                          )}
                          <MobileCardField label={t('orderHistory.products')} value={String(row.products)} divider />
                          <MobileCardField label={t('orderHistory.date')} value={row.date} />
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
          </div>

          {/* Right column: spend breakdowns (each maximize → ExpandModal). */}
          <div className="w-full flex flex-col gap-6 min-[1250px]:w-[32.5%]">
            {isAllRestaurants && (
              <ByRestaurantCard
                title={t('orderHistory.spendByRestaurant')}
                data={spendByRestaurantRows}
                loading={isOrdersLoading}
                isModalOpen={spendByRestaurantModalOpen}
                onMaximize={() => setSpendByRestaurantModalOpen(true)}
                onCloseModal={() => setSpendByRestaurantModalOpen(false)}
              />
            )}
            <div data-onboarding="orders-spend-supplier" data-onboarding-ready={!isOrdersLoading}>
              <SpendBySupplierCard data={supplierSpending} />
            </div>
            <div data-onboarding="orders-spend-category" data-onboarding-ready={!isOrdersLoading}>
              <SpendByCategoryDonut data={categorySpendBreakdown} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryPage;
